import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, unref as _unref, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "c-workflow-bio__inner" }
const _hoisted_3 = {
  key: 0,
  class: "c-workflow-bio__chart"
}
const _hoisted_4 = {
  key: 0,
  class: "c-workflow-bio__month-line c-workflow-bio__month-line--edge",
  "aria-hidden": "true"
}
const _hoisted_5 = {
  key: 1,
  class: "c-workflow-bio__month-line",
  "aria-hidden": "true"
}
const _hoisted_6 = {
  key: 2,
  class: "c-workflow-bio__month-line c-workflow-bio__month-line--edge",
  "aria-hidden": "true"
}
const _hoisted_7 = { class: "c-workflow-bio__chart-body" }
const _hoisted_8 = {
  key: 1,
  class: "c-workflow-bio__content"
}
const _hoisted_9 = {
  key: 0,
  class: "c-workflow-bio__panel"
}
const _hoisted_10 = {
  key: 0,
  class: "c-workflow-bio__panel-title"
}
const _hoisted_11 = {
  key: 1,
  class: "c-workflow-bio__bottom-title"
}

import { computed } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  WorkflowBioMonthFields,
  WorkflowBioPhaseFields,
  WorkflowBioProps,
} from '@/types/components/WorkflowBio';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

type WorkflowBioMonth = ScItem<WorkflowBioMonthFields> & { fields: WorkflowBioMonthFields };
type WorkflowBioPhase = ScItem<WorkflowBioPhaseFields> & { fields: WorkflowBioPhaseFields };


export default /*@__PURE__*/_defineComponent({
  __name: 'WorkflowBio',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;
const fields = computed(() => props.fields);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-workflow-bio'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const months = computed<WorkflowBioMonth[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'months');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowBioMonth => Boolean(item?.fields?.label?.value))
    : [];
});

const phases = computed<WorkflowBioPhase[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'phases');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowBioPhase => Boolean(item?.fields?.title?.value))
    : [];
});

const timelineStyle = computed(() => ({
  '--workflow-month-count': `${Math.max(months.value.length, 1)}`,
}));

const phaseGridStyle = computed(() => ({
  '--workflow-phase-columns': `${Math.max((months.value.length - 1) * 2, 1)}`,
}));

const toPositiveNumber = (value: number | string | undefined, fallback: number) => {
  const numericValue = Number(value ?? fallback);
  return Number.isFinite(numericValue) ? Math.max(1, numericValue) : fallback;
};

const COLOR_VALUE_PATTERN = /^(#|rgb\(|rgba\(|hsl\(|hsla\(|var\()/i;

const getColorValue = (value?: string) => {
  const color = value?.trim();
  return color && COLOR_VALUE_PATTERN.test(color) ? color : '';
};

const getMonthStyle = (month: WorkflowBioMonth) => {
  const color = getColorValue(month.fields.colorVariant?.value);
  return color ? { '--month-bg': color } : {};
};

const getPhaseStyle = (phase: WorkflowBioPhase) => {
  const startIndex = toPositiveNumber(phase.fields.startIndex?.value, 1);
  const span = toPositiveNumber(phase.fields.span?.value, 1);
  const row = toPositiveNumber(phase.fields.row?.value, 1);
  const color = getColorValue(phase.fields.colorVariant?.value);

  return {
    '--phase-start': `${startIndex}`,
    '--phase-span': `${span}`,
    '--phase-row': `${row}`,
    ...(color ? { '--phase-bg': color } : {}),
  };
};

const normalizeVariant = (variant?: string) => (variant || 'default').trim().toLowerCase();

const getVariantClass = (variant: string | undefined, block: 'month' | 'phase') =>
  getColorValue(variant) ? '' : `c-workflow-bio__${block}--${normalizeVariant(variant)}`;

return (_ctx: any,_cache: any) => {
  const _component_JssRichText = _resolveComponent("JssRichText")!
  const _component_ContentGrid = _resolveComponent("ContentGrid")!

  return (fields.value)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        style: _normalizeStyle(rootStyle.value),
        id: __props.rendering?.dataSource
      }, [
        _createVNode(_component_ContentGrid, {
          "desktop-default-grid": [1, 22],
          "tablet-default-grid": [1, 14],
          "mobile-default-grid": [1, 14]
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_JssRichText, {
                class: "c-workflow-bio__-title",
                field: fields.value?.title,
                tag: "h2"
              }, null, 8, ["field"]),
              (months.value.length || phases.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    (months.value.length)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: "c-workflow-bio__months",
                          style: _normalizeStyle(timelineStyle.value)
                        }, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(months.value, (month, index) => {
                            return (_openBlock(), _createElementBlock(_Fragment, {
                              key: month.id || month.fields.label?.value
                            }, [
                              (index === 0)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_4))
                                : _createCommentVNode("", true),
                              _createElementVNode("span", {
                                class: _normalizeClass(["c-workflow-bio__month", getVariantClass(month.fields.colorVariant?.value, 'month')]),
                                style: _normalizeStyle(getMonthStyle(month))
                              }, _toDisplayString(month.fields.label.value), 7),
                              (index < months.value.length - 1)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_5))
                                : _createCommentVNode("", true),
                              (index === months.value.length - 1)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_6))
                                : _createCommentVNode("", true)
                            ], 64))
                          }), 128))
                        ], 4))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_7, [
                      (phases.value.length)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            class: "c-workflow-bio__phases",
                            style: _normalizeStyle(phaseGridStyle.value)
                          }, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(phases.value, (phase) => {
                              return (_openBlock(), _createElementBlock("article", {
                                key: phase.id || phase.fields.title?.value,
                                class: _normalizeClass(["c-workflow-bio__phase", getVariantClass(phase.fields.colorVariant?.value, 'phase')]),
                                style: _normalizeStyle(getPhaseStyle(phase))
                              }, _toDisplayString(phase.fields.title.value), 7))
                            }), 128))
                          ], 4))
                        : _createCommentVNode("", true),
                      (fields.value.timelineNote?.value)
                        ? (_openBlock(), _createBlock(_unref(RichText), {
                            key: 1,
                            class: "c-workflow-bio__timeline-note",
                            field: fields.value.timelineNote
                          }, null, 8, ["field"]))
                        : _createCommentVNode("", true)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (fields.value.cardTitle?.value || fields.value.cardBullets?.value || fields.value.bottomTitle?.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    (fields.value.cardTitle?.value || fields.value.cardBullets?.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                          (fields.value.cardTitle?.value)
                            ? (_openBlock(), _createElementBlock("h3", _hoisted_10, _toDisplayString(fields.value.cardTitle.value), 1))
                            : _createCommentVNode("", true),
                          (fields.value.cardBullets?.value)
                            ? (_openBlock(), _createBlock(_unref(RichText), {
                                key: 1,
                                class: "c-workflow-bio__panel-list",
                                field: fields.value.cardBullets
                              }, null, 8, ["field"]))
                            : _createCommentVNode("", true)
                        ]))
                      : _createCommentVNode("", true),
                    (fields.value.bottomTitle?.value)
                      ? (_openBlock(), _createElementBlock("h3", _hoisted_11, _toDisplayString(fields.value.bottomTitle.value), 1))
                      : _createCommentVNode("", true),
                    (fields.value.bottomDescription?.value)
                      ? (_openBlock(), _createBlock(_unref(RichText), {
                          key: 2,
                          class: "c-workflow-bio__bottom-description",
                          field: fields.value.bottomDescription
                        }, null, 8, ["field"]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        })
      ], 14, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})